import { PropTypes } from "prop-types";
import { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import CookieHelper from "../Helpers/Cookies";
import { saveUser } from "../Redux/actions/userActions";
import {
  isHubEmbedded,
  initHubBridge,
  safeEmbedPath,
  postToHub,
  HUB_MSG,
} from "../Helpers/hubEmbed";

// Digadop 2.0 hub embed session bridge. Mounted once (like LoginCheck), renders
// nothing. When the app is embedded in the Janus hub it:
//   - announces READY and accepts a hub-posted session, persisting it exactly
//     as the Salesforce login does (cookie keyed <orgId>.<userId> + cur_user)
//     so a reload re-authenticates through the normal LoginCheck path;
//   - honours a hub deep-link (navigate-to);
//   - reports its own route changes and content height back up to the hub so the
//     shell can keep the URL and the iframe size in sync.
// Exported unconnected for tests (#437). The default export stays the
// withRouter+connect wrapper the app mounts; tests need the raw class so they
// can assert what acceptSession and clearSessionForHubLogout actually DO.
export class HubBridge extends Component {
  componentDidMount() {
    if (!isHubEmbedded()) {
      return;
    }
    this.cleanup = initHubBridge({
      onSession: (user) => this.acceptSession(user),
      onHubLogout: () => this.clearSessionForHubLogout(),
      onNavigateTo: (path) => {
        // #411 Phase 3.1: refuse anything that is not an app-relative route.
        const safe = safeEmbedPath(path);
        if (!safe || !this.props.history) return;
        // #411 Phase 3.2: REPLACE, do not push. The hub owns the URL, so it has
        // already created a history entry for this navigation. Pushing here adds
        // a SECOND entry to the top-level browser history for one user action,
        // which is what makes browser Back behave unexpectedly inside the hub.
        this.props.history.replace(safe);
        // #411 Phase 3.6: a hub-driven route change swaps the frame's content
        // with no visible page load, so a screen reader user gets no signal that
        // anything happened. Move focus to the main landmark, which is the
        // iframe equivalent of "the page changed". Guarded because the element
        // only exists in embedded mode.
        if (typeof window !== "undefined" && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => {
            const main = document.getElementById("hub-embed-main");
            if (main && typeof main.focus === "function") main.focus();
          });
        }
      },
    });
    this.postHeight();
    // Report height so the hub can auto-size the iframe. A ResizeObserver on the
    // body catches content-driven height changes (a list expanding, a modal, a
    // route swap) that a window resize listener misses; fall back to resize when
    // ResizeObserver is unavailable.
    this.onResize = () => this.postHeight();
    if (typeof window.ResizeObserver === "function" && document.body) {
      this.ro = new window.ResizeObserver(this.onResize);
      this.ro.observe(document.body);
    } else {
      window.addEventListener("resize", this.onResize);
    }
    // #411 Phase 3.1: report the INITIAL route, not just later changes. Without
    // this the hub cannot sync its shell URL to wherever the embed actually
    // started, so a hub URL could never be shared or bookmarked to a specific
    // admin screen.
    this.lastPath = this.currentPath();
    postToHub(HUB_MSG.NAVIGATED, { path: this.lastPath });
  }

  componentDidUpdate() {
    if (!isHubEmbedded()) {
      return;
    }
    const path = this.currentPath();
    if (path !== this.lastPath) {
      this.lastPath = path;
      postToHub(HUB_MSG.NAVIGATED, { path });
    }
    this.postHeight();
  }

  componentWillUnmount() {
    if (this.cleanup) {
      this.cleanup();
    }
    if (this.ro) {
      this.ro.disconnect();
    } else if (this.onResize) {
      window.removeEventListener("resize", this.onResize);
    }
  }

  currentPath() {
    const loc = this.props.router || {};
    return (loc.pathname || "") + (loc.search || "");
  }

  postHeight() {
    try {
      const height = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      // De-dup: ResizeObserver + componentDidUpdate can fire rapidly. Only post
      // when the height actually moved (more than a couple of px) so the hub is
      // not flooded with identical messages.
      if (this.lastHeight != null && Math.abs(height - this.lastHeight) < 2) {
        return;
      }
      this.lastHeight = height;
      postToHub(HUB_MSG.HEIGHT, { height });
    } catch (err) {
      /* ignore */
    }
  }

  // Persist a hub-provided session the same way LoginScreen.socketonCallback
  // does, so the rest of the app (LoginCheck on reload, account swap, the
  // sandbox gate) works unchanged. The user object shape is the react-client
  // session: { orgId, userId, token, roleId, name, IsSandbox, ... }.
  // #411 Phase 3.3: drop the embedded session because the HUB signed out.
  // Mirrors what acceptSession persists, so nothing authenticated survives:
  // the per-account cookie, the current-user pointer, and the redux user.
  clearSessionForHubLogout() {
    try {
      const cur = CookieHelper.getCookie("cur_user");
      if (cur) CookieHelper.setCookie(cur, "");
      CookieHelper.setCookie("cur_user", "");
    } catch (err) {
      // Cookie access can throw in a partitioned frame; clearing redux below
      // still de-authenticates this tab, which is the security-relevant part.
    }
    this.props.saveUser({});
  }

  acceptSession(user) {
    if (!user || !user.orgId || !user.userId || !user.token) {
      return;
    }
    const key = user.orgId + "." + user.userId;
    CookieHelper.setCookie(key, JSON.stringify(user));
    CookieHelper.setCookie("cur_user", key);
    let curAccounts = CookieHelper.getCookie("cur_accounts");
    try {
      curAccounts = curAccounts ? JSON.parse(curAccounts) : [];
    } catch (err) {
      curAccounts = [];
    }
    if (curAccounts.indexOf(key) === -1) {
      curAccounts.push(key);
      CookieHelper.setCookie("cur_accounts", JSON.stringify(curAccounts));
    }
    this.props.saveUser(user);
    postToHub(HUB_MSG.SESSION_ACK, { orgId: user.orgId, userId: user.userId });
  }

  render() {
    return null;
  }
}

HubBridge.propTypes = {
  saveUser: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
  router: state.router.location,
});

export default withRouter(connect(mapStateToProps, { saveUser })(HubBridge));
